import { Color, Style } from "./alert-variants.ts";
import { Root, createRoot } from "react-dom/client";
import { Alert } from "./alert.tsx";

interface PopupAlertProps {
  message: string;
  color: Color;
  style: Style;
  title?: string;
}

let alertReactRoot: Root | null = null;
let rootElement: HTMLElement | null = null;
let destroyTimeout: number | null = null;

const destroyRoot = () => {
  alertReactRoot?.unmount();
  rootElement?.remove();
  alertReactRoot = null;
  rootElement = null;
};

const scheduleDestroy = (ms: number) => {
  window.clearTimeout(destroyTimeout ?? undefined);
  destroyTimeout = window.setTimeout(destroyRoot, ms);
};

const createRootIfNotExist = (): Root => {
  if (!alertReactRoot || !rootElement) {
    const element = document.createElement("div");
    element.id = "alert-root";
    document.body.appendChild(element);
    rootElement = element;
    alertReactRoot = createRoot(element);
  }
  rootElement.className =
    "twp fixed left-1/2 bottom-[20px] md:bottom-[40px] z-[99999] -translate-x-1/2 w-[400px] max-w-[calc(100vw-32px)]";
  return alertReactRoot;
};

export const showPopupAlertFromLocalStorage = () => {
  const alertJson: string | null = window.sessionStorage.getItem("alert");

  // only show alert if there's something to show
  if (!alertJson) {
    return;
  }
  window.sessionStorage.removeItem("alert");
  const alert = JSON.parse(alertJson) as PopupAlertProps;
  // add a root at the end of the body
  const reactRoot = createRootIfNotExist();
  reactRoot.render(
    <Alert
      color={alert.color}
      style={alert.style}
      message={alert.message}
      title={alert.title}
      dismissible
      onDismiss={destroyRoot}
      renderLeadingIcon={() => <i className="fa-sharp fa-regular fa-shield-check" />}
    />,
  );
  scheduleDestroy(5000);
};

export const setPopupAlertInLocalStorage = (alert: PopupAlertProps) => {
  window.sessionStorage.setItem("alert", JSON.stringify(alert));
};

export const showErrorMessage = (title: string, message: string) => {
  const reactRoot = createRootIfNotExist();
  reactRoot.render(
    <Alert color={"red"} style="outlined" title={title} message={message} dismissible onDismiss={destroyRoot} />,
  );
  scheduleDestroy(15000);
};

export const showSuccessMessage = (message: string) => {
  const reactRoot = createRootIfNotExist();
  reactRoot.render(
    <Alert
      color={"green"}
      style={"solid"}
      message={message}
      dismissible
      onDismiss={destroyRoot}
      renderLeadingIcon={() => <i className="fa-sharp fa-regular fa-shield-check" />}
    />,
  );
  scheduleDestroy(5000);
};
